import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Avatar,
  LinearProgress,
  useMediaQuery,
  useTheme,
  Stack,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  People,
  PeopleOutline,
  HourglassEmpty,
  Class,
  TrendingUp,
} from '@mui/icons-material';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import AdminLayout from '../../components/AdminLayout';
import axios from 'axios';
import { toast } from 'react-toastify';

const StatCard = ({ title, value, icon, color, subtitle, onClick }) => (
  <Card
    sx={{
      height: '100%',
      position: 'relative',
      overflow: 'hidden',
      background: 'linear-gradient(135deg, #FFFFFF 0%, #FFF9F0 100%)',
      border: '1px solid',
      borderColor: 'rgba(212, 175, 55, 0.2)',
      boxShadow: '0 2px 8px rgba(212, 175, 55, 0.15)',
      transition: 'all 0.3s ease',
      cursor: onClick ? 'pointer' : 'default',
      '&:hover': {
        transform: onClick ? 'translateY(-4px)' : 'none',
        boxShadow: onClick ? '0 4px 16px rgba(212, 175, 55, 0.25)' : '0 2px 8px rgba(212, 175, 55, 0.15)',
      },
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        width: '4px',
        height: '100%',
        background: 'linear-gradient(180deg, #D4AF37 0%, #F4D03F 100%)',
      },
    }}
    onClick={onClick}
  >
    <CardContent sx={{ pl: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="body2" sx={{ color: '#8B7355', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.7rem' }} gutterBottom>
            {title}
          </Typography>
          <Typography variant="h4" fontWeight={700} sx={{ color: '#D4AF37' }} gutterBottom>
            {value}
          </Typography>
          {subtitle && (
            <Typography variant="caption" sx={{ color: '#8B7355' }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        <Avatar
          sx={{
            background: 'linear-gradient(135deg, #D4AF37 0%, #F4D03F 100%)',
            boxShadow: '0 2px 8px rgba(212, 175, 55, 0.3)',
            width: 56,
            height: 56,
          }}
        >
          {icon}
        </Avatar>
      </Box>
    </CardContent>
  </Card>
);

const Dashboard = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [chartsLoading, setChartsLoading] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [stats, setStats] = useState({
    total_students: 0,
    active_students: 0,
    pending_approvals: 0,
    total_batches: 0,
    total_revenue: 0,
    pending_payments: 0,
    collection_rate: 0,
    recent_payments: [],
    enrollment_data: [],
    batch_distribution: [],
    revenue_data: [],
    is_filtered: false,
  });

  const monthOptions = React.useMemo(() => {
    const options = [{ label: 'All Time', value: 'all', month: null, year: null }];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const month = d.getMonth() + 1;
      const year = d.getFullYear();
      options.push({
        label: d.toLocaleString('default', { month: 'long', year: 'numeric' }),
        value: `${month}-${year}`,
        month,
        year,
      });
    }
    return options;
  }, []);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async (month, year) => {
    try {
      const params = {};
      if (month && year) {
        params.month = month;
        params.year = year;
      }
      const response = await axios.get('/api/admin/dashboard', { params });
      setStats(response.data);
    } catch (error) {
      toast.error('Failed to load dashboard data');
      console.error(error);
    } finally {
      setLoading(false);
      setChartsLoading(false);
    }
  };

  const handleFilterChange = (event) => {
    const value = event.target.value;
    setSelectedFilter(value);
    setChartsLoading(true);
    if (value === 'all') {
      fetchDashboardData();
    } else {
      const option = monthOptions.find((opt) => opt.value === value);
      fetchDashboardData(option.month, option.year);
    }
  };

  const selectedLabel = monthOptions.find((opt) => opt.value === selectedFilter)?.label || 'All Time';

  if (loading) {
    return (
      <AdminLayout>
        <LinearProgress />
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <Box sx={{ width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
        {/* Welcome Section */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            Welcome back! 👋
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Here's what's happening with your academy today
          </Typography>
        </Box>

        {/* Stats Cards */}
        <Grid container spacing={3} sx={{ mb: 4, width: '100%', ml: 0 }}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Total Students"
              value={stats.total_students}
              icon={<People />}
              color="primary.main"
              subtitle="+12% from last month"
              onClick={() => navigate('/admin/students')}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Active Students"
              value={stats.active_students}
              icon={<PeopleOutline />}
              color="success.main"
              subtitle="Currently enrolled"
              onClick={() => navigate('/admin/students')}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Pending Approvals"
              value={stats.pending_approvals}
              icon={<HourglassEmpty />}
              color="warning.main"
              subtitle="Awaiting review"
              onClick={() => navigate('/admin/students?filter=pending')}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Active Batches"
              value={stats.total_batches}
              icon={<Class />}
              color="info.main"
              subtitle="Running classes"
              onClick={() => navigate('/admin/batches')}
            />
          </Grid>
        </Grid>

        {/* Charts Filter */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="h6" fontWeight={600} sx={{ color: '#2C2416' }}>
            Overview
          </Typography>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel id="dashboard-month-filter-label">Month</InputLabel>
            <Select
              labelId="dashboard-month-filter-label"
              label="Month"
              value={selectedFilter}
              onChange={handleFilterChange}
            >
              {monthOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        {chartsLoading && <LinearProgress sx={{ mb: 2 }} />}

        {/* Charts */}
        <Grid container spacing={3} sx={{ mb: 4, width: '100%', ml: 0 }}>
          {/* Enrollment Trend */}
          <Grid item xs={12} md={8}>
            <Card sx={{
              position: 'relative',
              overflow: 'hidden',
              background: 'linear-gradient(135deg, #FFFFFF 0%, #FFF9F0 100%)',
              border: '1px solid',
              borderColor: 'rgba(212, 175, 55, 0.2)',
              boxShadow: '0 2px 8px rgba(212, 175, 55, 0.15)',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                width: '4px',
                height: '100%',
                background: 'linear-gradient(180deg, #D4AF37 0%, #F4D03F 100%)',
              },
            }}>
              <CardContent sx={{ pl: 3 }}>
                <Typography variant="h6" fontWeight={600} sx={{ color: '#2C2416' }} gutterBottom>
                  Student Enrollment Trend
                </Typography>
                {stats.is_filtered && (
                  <Typography variant="caption" sx={{ color: '#8B7355' }}>
                    6 months ending {selectedLabel}
                  </Typography>
                )}
                <Box sx={{ width: '100%', height: { xs: 250, sm: 300 }, mt: 2 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={stats.enrollment_data} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis 
                        dataKey="month" 
                        stroke="#999" 
                        tick={{ fontSize: 12 }}
                        angle={-45}
                        textAnchor="end"
                        height={60}
                      />
                      <YAxis stroke="#999" tick={{ fontSize: 12 }} />
                      <Tooltip
                        contentStyle={{
                          background: 'white',
                          border: '1px solid #ddd',
                          borderRadius: 8,
                          fontSize: 12,
                        }}
                      />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                      <Line
                        type="monotone"
                        dataKey="students"
                        stroke="#8B4513"
                        strokeWidth={2}
                        dot={{ fill: '#8B4513', r: 4 }}
                        activeDot={{ r: 6 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Batch Distribution */}
          <Grid item xs={12} md={4}>
            <Card sx={{ 
              height: '100%',
              position: 'relative',
              overflow: 'hidden',
              background: 'linear-gradient(135deg, #FFFFFF 0%, #FFF9F0 100%)',
              border: '1px solid',
              borderColor: 'rgba(212, 175, 55, 0.2)',
              boxShadow: '0 2px 8px rgba(212, 175, 55, 0.15)',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                width: '4px',
                height: '100%',
                background: 'linear-gradient(180deg, #D4AF37 0%, #F4D03F 100%)',
              },
            }}>
              <CardContent sx={{ pl: 3 }}>
                <Typography variant="h6" fontWeight={600} sx={{ color: '#2C2416' }} gutterBottom>
                  {stats.is_filtered ? 'Fee Collection by Batch' : 'Batch Distribution'}
                </Typography>
                {stats.is_filtered && (
                  <Typography variant="caption" sx={{ color: '#8B7355' }}>
                    Collection rate for {selectedLabel}
                  </Typography>
                )}
                {stats.batch_distribution.length > 0 ? (
                  <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', mt: 2 }}>
                    {/* Pie Chart */}
                    <Box sx={{ width: '100%', height: 220 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={stats.batch_distribution}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={({ percent }) => percent > 0.05 ? `${(percent * 100).toFixed(0)}%` : ''}
                            outerRadius="80%"
                            fill="#8884d8"
                            dataKey="value"
                            strokeWidth={2}
                            stroke="#fff"
                          >
                            {stats.batch_distribution.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                          </Pie>
                          <Tooltip
                            contentStyle={{
                              fontSize: 12,
                              borderRadius: 8,
                              border: '1px solid rgba(212, 175, 55, 0.3)',
                              boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                            }}
                            formatter={(value) => stats.is_filtered ? `${value}%` : value}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    </Box>
                    
                    {/* Custom Legend */}
                    <Box sx={{ 
                      maxHeight: 180, 
                      overflowY: 'auto',
                      mt: 1,
                      pr: 1,
                      '&::-webkit-scrollbar': {
                        width: '6px',
                      },
                      '&::-webkit-scrollbar-track': {
                        background: '#f1f1f1',
                        borderRadius: '10px',
                      },
                      '&::-webkit-scrollbar-thumb': {
                        background: '#D4AF37',
                        borderRadius: '10px',
                      },
                      '&::-webkit-scrollbar-thumb:hover': {
                        background: '#B8941F',
                      },
                    }}>
                      {stats.batch_distribution.map((entry, index) => (
                        <Box 
                          key={`legend-${index}`}
                          sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            mb: 0.8,
                            p: 0.5,
                            borderRadius: 1,
                            '&:hover': {
                              bgcolor: 'rgba(212, 175, 55, 0.05)',
                            }
                          }}
                        >
                          <Box 
                            sx={{ 
                              width: 12, 
                              height: 12, 
                              bgcolor: entry.color, 
                              borderRadius: '2px',
                              mr: 1,
                              flexShrink: 0,
                              border: '1px solid rgba(0,0,0,0.1)'
                            }} 
                          />
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              fontSize: '0.75rem',
                              color: '#2C2416',
                              flexGrow: 1,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              mr: 1
                            }}
                          >
                            {entry.name}
                          </Typography>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              fontSize: '0.75rem',
                              fontWeight: 600,
                              color: '#D4AF37',
                              flexShrink: 0
                            }}
                          >
                            {stats.is_filtered ? `${entry.value}%` : entry.value}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
                    <Typography variant="body2" color="text.secondary">
                      No batch data available
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Revenue Section */}
        <Grid container spacing={3} sx={{ mb: 4, width: '100%', ml: 0 }}>
          <Grid item xs={12} md={8}>
            <Card sx={{
              position: 'relative',
              overflow: 'hidden',
              background: 'linear-gradient(135deg, #FFFFFF 0%, #FFF9F0 100%)',
              border: '1px solid',
              borderColor: 'rgba(212, 175, 55, 0.2)',
              boxShadow: '0 2px 8px rgba(212, 175, 55, 0.15)',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                width: '4px',
                height: '100%',
                background: 'linear-gradient(180deg, #D4AF37 0%, #F4D03F 100%)',
              },
            }}>
              <CardContent sx={{ pl: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                  <Box>
                    <Typography variant="h6" fontWeight={600} sx={{ color: '#2C2416' }}>
                      {stats.is_filtered ? 'Revenue by Batch' : 'Revenue Overview'}
                    </Typography>
                    {stats.is_filtered && (
                      <Typography variant="caption" sx={{ color: '#8B7355' }}>
                        {selectedLabel}
                      </Typography>
                    )}
                  </Box>
                  {!stats.is_filtered && stats.revenue_data.length > 1 && (
                    <Chip
                      icon={<TrendingUp />}
                      label={`${stats.revenue_data.length > 0 && stats.revenue_data[stats.revenue_data.length - 1].revenue > 0
                        ? '+' + (((stats.revenue_data[stats.revenue_data.length - 1].revenue - (stats.revenue_data[stats.revenue_data.length - 2]?.revenue || 0)) / (stats.revenue_data[stats.revenue_data.length - 2]?.revenue || 1)) * 100).toFixed(0) + '%'
                        : '0%'} this month`}
                      color="success"
                      size="small"
                    />
                  )}
                </Box>
                <Box sx={{ width: '100%', height: { xs: 220, sm: 250 }, mt: 2 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={stats.revenue_data} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis 
                        dataKey="month" 
                        stroke="#999" 
                        tick={{ fontSize: 12 }}
                        angle={-45}
                        textAnchor="end"
                        height={60}
                      />
                      <YAxis stroke="#999" tick={{ fontSize: 12 }} />
                      <Tooltip
                        contentStyle={{
                          background: 'white',
                          border: '1px solid #ddd',
                          borderRadius: 8,
                          fontSize: 12,
                        }}
                        formatter={(value) => `₹${value.toLocaleString()}`}
                      />
                      <Bar dataKey="revenue" fill="#D4AF37" radius={[8, 8, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card sx={{ 
              height: '100%',
              position: 'relative',
              overflow: 'hidden',
              background: 'linear-gradient(135deg, #FFFFFF 0%, #FFF9F0 100%)',
              border: '1px solid',
              borderColor: 'rgba(212, 175, 55, 0.2)',
              boxShadow: '0 2px 8px rgba(212, 175, 55, 0.15)',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                width: '4px',
                height: '100%',
                background: 'linear-gradient(180deg, #D4AF37 0%, #F4D03F 100%)',
              },
            }}>
              <CardContent sx={{ pl: 3 }}>
                <Typography variant="h6" fontWeight={600} sx={{ color: '#2C2416' }} gutterBottom>
                  Payment Summary
                </Typography>
                {stats.is_filtered && (
                  <Typography variant="caption" sx={{ color: '#8B7355' }}>
                    {selectedLabel}
                  </Typography>
                )}
                <Box sx={{ mt: 3 }}>
                  <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        {stats.is_filtered ? 'Revenue This Month' : 'Total Revenue'}
                      </Typography>
                      <Typography variant="h6" fontWeight={700} color="success.main">
                        ₹{stats.total_revenue.toLocaleString()}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Pending Payments
                      </Typography>
                      <Typography variant="h6" fontWeight={700} color="warning.main">
                        {stats.pending_payments}
                      </Typography>
                    </Box>
                  </Box>
                  <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Collection Rate
                      </Typography>
                      <Typography variant="h6" fontWeight={700} color="primary.main">
                        {stats.collection_rate}%
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Recent Payments Table */}
        <Card sx={{
          position: 'relative',
          overflow: 'hidden',
          background: 'linear-gradient(135deg, #FFFFFF 0%, #FFF9F0 100%)',
          border: '1px solid',
          borderColor: 'rgba(212, 175, 55, 0.2)',
          boxShadow: '0 2px 8px rgba(212, 175, 55, 0.15)',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            width: '4px',
            height: '100%',
            background: 'linear-gradient(180deg, #D4AF37 0%, #F4D03F 100%)',
          },
        }}>
          <CardContent sx={{ pl: 3 }}>
            <Typography variant="h6" fontWeight={600} sx={{ color: '#2C2416' }} gutterBottom>
              Recent Payments
            </Typography>
            {isMobile ? (
              /* Mobile Card View */
              <Stack spacing={2} sx={{ mt: 2 }}>
                {stats.recent_payments.length > 0 ? (
                  stats.recent_payments.map((payment) => (
                    <Card 
                      key={payment.id} 
                      sx={{
                        position: 'relative',
                        overflow: 'hidden',
                        background: 'linear-gradient(135deg, #FFFFFF 0%, #FFF9F0 100%)',
                        border: '1px solid',
                        borderColor: 'rgba(212, 175, 55, 0.2)',
                        boxShadow: '0 2px 8px rgba(212, 175, 55, 0.15)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          boxShadow: '0 4px 16px rgba(212, 175, 55, 0.25)',
                          transform: 'translateY(-2px)',
                        },
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '4px',
                          height: '100%',
                          background: 'linear-gradient(180deg, #D4AF37 0%, #F4D03F 100%)',
                        },
                      }}
                    >
                      <CardContent sx={{ pl: 3 }}>
                        <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#2C2416', fontSize: '1.1rem' }} gutterBottom>
                          {payment.student_name}
                        </Typography>
                        
                        <Divider sx={{ my: 1.5, borderColor: 'rgba(212, 175, 55, 0.15)' }} />
                        
                        <Stack spacing={1.5}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="caption" sx={{ color: '#8B7355', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.7rem' }}>Amount</Typography>
                            <Typography variant="body2" fontWeight={700} sx={{ color: '#D4AF37' }}>₹{payment.amount}</Typography>
                          </Box>
                          
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            <Chip 
                              label={payment.payment_type} 
                              size="small" 
                              sx={{ bgcolor: 'rgba(212, 175, 55, 0.1)', color: '#8B4513', borderColor: 'rgba(212, 175, 55, 0.3)', fontWeight: 600 }}
                              variant="outlined"
                            />
                            <Chip label={payment.payment_method} size="small" variant="outlined" />
                            <Chip
                              label={payment.status}
                              size="small"
                              color={
                                payment.status === 'paid'
                                  ? 'success'
                                  : payment.status === 'pending'
                                  ? 'warning'
                                  : 'error'
                              }
                            />
                          </Box>
                          
                          <Typography variant="caption" sx={{ color: '#8B7355', fontSize: '0.75rem' }}>
                            {new Date(payment.created_at).toLocaleDateString()}
                          </Typography>
                        </Stack>
                      </CardContent>
                    </Card>
                  ))
                ) : (
                  <Typography variant="body2" color="text.secondary" align="center" py={3}>
                    No recent payments
                  </Typography>
                )}
              </Stack>
            ) : (
              /* Desktop Table View */
              <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Student Name</TableCell>
                    <TableCell>Amount</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Method</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stats.recent_payments.length > 0 ? (
                    stats.recent_payments.map((payment) => (
                      <TableRow key={payment.id}>
                        <TableCell>{payment.student_name}</TableCell>
                        <TableCell>₹{payment.amount}</TableCell>
                        <TableCell>
                          <Chip label={payment.payment_type} size="small" />
                        </TableCell>
                        <TableCell>{payment.payment_method}</TableCell>
                        <TableCell>
                          <Chip
                            label={payment.status}
                            size="small"
                            color={
                              payment.status === 'paid'
                                ? 'success'
                                : payment.status === 'pending'
                                ? 'warning'
                                : 'error'
                            }
                          />
                        </TableCell>
                        <TableCell>
                          {new Date(payment.created_at).toLocaleDateString()}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <Typography variant="body2" color="text.secondary">
                          No recent payments
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            )}
          </CardContent>
        </Card>
      </Box>
    </AdminLayout>
  );
};

export default Dashboard;
